import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  afterNextRender,
} from '@angular/core';
import {
  LearningQuizFormComponent,
  LearningQuizFormValue,
} from './learning-quiz-form/learning-quiz-form.component';

// API Quiz Data Interface
export interface QuizData {
  id: string;
  _path: string;
  quizTitle: string;
  questionPath: any[];
  suggestedTime?: number;
  level?: string;
  toolsRequired?: string;
}

@Component({
  selector: 'app-learning-quiz',
  templateUrl: './learning-quiz-initial.component.html',
  styleUrls: ['./learning-quiz-initial.component.scss'],
  standalone: false,
})
export class LearningQuizInitialComponent implements OnInit, OnDestroy {
  @Input() lessonId?: string;
  @Input() lessonTitle?: string;
  @Input() quizData?: QuizData; // API quiz data ONLY
  @Output() backToLesson = new EventEmitter<void>();
  @Output() backToLibrary = new EventEmitter<void>();
  @Output() startQuizEvent = new EventEmitter<LearningQuizFormValue>();

  @ViewChild('backWarningDialog') backWarningDialog: any;
  @ViewChild('backToLibraryBtn', { read: ElementRef })
  backToLibraryBtn?: ElementRef<HTMLElement>;
  @ViewChild(LearningQuizFormComponent) quizForm?: LearningQuizFormComponent;

  displayLessonTitle: string = '';
  totalQuestions: number = 0;
  estimatedTime: number = 3;
  toolsRequired: string = '';
  isXsScreen: boolean = false;

  private readonly injector = inject(Injector);

  constructor() {}

  ngOnInit() {
    this.checkScreenSize();
    window.addEventListener('resize', this.onResize);
    this.loadLessonContent();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    afterNextRender(
      () => {
        this.backToLibraryBtn?.nativeElement?.focus();
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.onResize);
  }

  private onResize = () => {
    this.checkScreenSize();
  };

  private checkScreenSize() {
    this.isXsScreen = window.innerWidth < 576;
  }

  // Start Assessment acts as the quiz form's submit button; startQuiz runs once the form is valid
  onStartAssessment() {
    this.quizForm?.submit();
  }

  startQuiz(formValue?: LearningQuizFormValue) {
    this.startQuizEvent.emit(formValue);
  }

  goBack() {
    this.backWarningDialog?.openModalDialog();
  }

  confirmGoBack() {
    this.backToLibrary.emit();
  }

  onBackToLesson() {
    this.backToLesson.emit();
  }

  loadLessonContent() {
    if (this.lessonTitle) {
      this.displayLessonTitle = this.lessonTitle;
    }

    if (this.quizData) {
      // Get total questions from API
      this.totalQuestions = this.quizData.questionPath?.length || 0;

      // Get estimated time from API suggestedTime, or calculate as fallback
      this.estimatedTime =
        this.quizData.suggestedTime ||
        Math.max(1, Math.ceil(this.totalQuestions / 2));

      // Get tools required from API (e.g., "Calculator")
      this.toolsRequired = this.quizData.toolsRequired || '';
    }
  }
}
